import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import * as pdfjsLib from 'pdfjs-dist';

@Component({
  selector: 'app-pdf-book-viewer',
  templateUrl: './pdf-book-viewer.component.html',
  styleUrls: ['./pdf-book-viewer.component.scss']
})
export class PdfBookViewerComponent implements OnInit, OnDestroy {

  pdfUrl: string;
  title: string;
  totalPages = 0;
  currentSpread = 0; // índice del “doble spread” (0 = páginas 0-1, 1 = 2-3, …)
  pageImages: string[] = []; // data URLs de cada página
  loading = true;
  error: string;
  flipping = false;
  flipDirection: 'next' | 'prev';
  useIframeFallback = false;
  private pdfDoc: any;
  private scale = 1.8;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private sanitizer: DomSanitizer
  ) {
    if (typeof (pdfjsLib as any).GlobalWorkerOptions !== 'undefined') {
      (pdfjsLib as any).GlobalWorkerOptions.workerSrc = 'pdf.worker.js';
    }
  }

  ngOnInit() {
    this.route.queryParams.subscribe(params => {
      this.pdfUrl = params.pdf || '';
      this.title = params.title || 'Documento';
      this.useIframeFallback = false;
      if (this.pdfUrl) {
        this.loadPdf();
      } else {
        this.error = 'No se especificó la URL del documento.';
        this.loading = false;
      }
    });
  }

  ngOnDestroy() {
    if (this.pdfDoc) {
      this.pdfDoc.destroy();
    }
  }

  get safePdfUrl(): SafeResourceUrl {
    return this.pdfUrl ? this.sanitizer.bypassSecurityTrustResourceUrl(this.pdfUrl) : null;
  }

  private async loadPdf() {
    this.loading = true;
    this.error = null;
    this.useIframeFallback = false;
    try {
      const loadingTask = pdfjsLib.getDocument({ url: this.pdfUrl });
      this.pdfDoc = await loadingTask.promise;
      this.totalPages = this.pdfDoc.numPages;
      await this.renderAllPages();
      this.loading = false;
    } catch (e) {
      this.loading = false;
      this.useIframeFallback = true;
      console.warn('PDF.js no pudo cargar el documento, mostrando en iframe:', e);
    }
  }

  private async renderAllPages() {
    this.pageImages = [];
    for (let i = 1; i <= this.totalPages; i++) {
      this.pageImages.push(await this.renderPage(i));
    }
  }

  private async renderPage(num: number): Promise<string> {
    const page = await this.pdfDoc.getPage(num);
    const viewport = page.getViewport({ scale: this.scale });
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return '';
    canvas.height = viewport.height;
    canvas.width = viewport.width;
    const renderTask = page.render({ canvasContext: ctx, viewport });
    await renderTask.promise;
    return canvas.toDataURL('image/jpeg', 0.85);
  }

  get leftPageIndex(): number {
    return this.currentSpread * 2;
  }

  get rightPageIndex(): number {
    return this.currentSpread * 2 + 1;
  }

  get leftPageSrc(): string {
    return this.pageImages[this.leftPageIndex] || '';
  }

  get rightPageSrc(): string {
    return this.pageImages[this.rightPageIndex] || '';
  }

  get nextLeftPageSrc(): string {
    const idx = (this.currentSpread + 1) * 2;
    return this.pageImages[idx] || '';
  }

  get nextRightPageSrc(): string {
    const idx = (this.currentSpread + 1) * 2 + 1;
    return this.pageImages[idx] || '';
  }

  get prevLeftPageSrc(): string {
    const idx = (this.currentSpread - 1) * 2;
    return this.pageImages[idx] || '';
  }

  get prevRightPageSrc(): string {
    const idx = (this.currentSpread - 1) * 2 + 1;
    return this.pageImages[idx] || '';
  }

  get canGoNext(): boolean {
    // Hay al menos una página más en el siguiente spread
    return !this.flipping && (this.currentSpread + 1) * 2 + 1 < this.totalPages;
  }

  get canGoPrev(): boolean {
    return !this.flipping && this.currentSpread > 0;
  }

  get pageRangeLabel(): string {
    if (this.rightPageIndex > this.leftPageIndex) {
      return `Páginas ${this.leftPageIndex + 1}-${this.rightPageIndex + 1}`;
    }
    return `Página ${this.leftPageIndex + 1}`;
  }

  nextPage() {
    if (!this.canGoNext) return;
    this.flipping = true;
    this.flipDirection = 'next';
    setTimeout(() => {
      this.currentSpread++;
      this.flipping = false;
    }, 650);
  }

  prevPage() {
    if (!this.canGoPrev) return;
    this.flipping = true;
    this.flipDirection = 'prev';
    setTimeout(() => {
      this.currentSpread--;
      this.flipping = false;
    }, 650);
  }

  goBack() {
    this.router.navigate(['/publicaciones']);
  }
}
